import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Download, Pencil, Plus, RefreshCw, Search, Trash2, X } from 'lucide-react';
import { adminService, ComponentPairRow, PickMemoryRow } from '../services/AdminService';

// Admin view of what the autopilot remembers (routes/admin.py "PICK MEMORY"):
//  • Picks — per organisation + catalog + vehicle family (VIN[0:8] + model-year
//    code), the part a request line resolved to. Seeded from a customer's
//    purchase history, written live when a person answers a question.
//  • Equivalences — two component readings learned to name the same part on a
//    catalog ("bumper cover" = "bumper fascia").
// Rows with no organisation are shared by every organisation. A row switched
// off is kept but ignored by the scorer; a deleted row is gone.
const PAGE = 100;

type View = 'picks' | 'pairs';

const Toggle: React.FC<{ on: boolean; onChange: (v: boolean) => void; title?: string }> = ({ on, onChange, title }) => (
    <button
        type="button"
        onClick={() => onChange(!on)}
        title={title || (on ? 'Enabled — click to switch off' : 'Off — click to enable')}
        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${on ? 'bg-green-500' : 'bg-gray-300'}`}
    >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${on ? 'translate-x-4' : 'translate-x-0.5'}`} />
    </button>
);

interface MemoryTabProps {
    /** show the picks / equivalences a selection profile learned, instead of the org / shared rows */
    profileId?: number;
    compact?: boolean;
}

const MemoryTab: React.FC<MemoryTabProps> = ({ profileId, compact }) => {
    const [view, setView] = useState<View>('picks');
    const [orgs, setOrgs] = useState<{ id: number; name: string }[]>([]);
    const [orgId, setOrgId] = useState('');          // '' any · 'shared' · '<id>'
    const [catalog, setCatalog] = useState('');
    const [qDraft, setQDraft] = useState('');
    const [q, setQ] = useState('');
    const [source, setSource] = useState('');
    const [enabledF, setEnabledF] = useState<'' | 'true' | 'false'>('');
    const [offset, setOffset] = useState(0);
    const [picks, setPicks] = useState<PickMemoryRow[]>([]);
    const [pairs, setPairs] = useState<ComponentPairRow[]>([]);
    const [total, setTotal] = useState(0);
    const [summary, setSummary] = useState<{ autopilot_enabled: boolean; org_memory_applied?: boolean; org_memory_recorded?: boolean; picks: any[]; pairs: any[] } | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [msg, setMsg] = useState<string | null>(null);
    // inline edit (picks)
    const [editing, setEditing] = useState<number | null>(null);
    const [draft, setDraft] = useState({ part_id: '', part_name: '' });
    const [saving, setSaving] = useState(false);
    // add row
    const [showAdd, setShowAdd] = useState(false);
    const [addPick, setAddPick] = useState({ org: '', catalog: '', vin: '', line: '', part_id: '', part_name: '' });
    const [addPair, setAddPair] = useState({ org: '', catalog: '', a: '', b: '' });

    useEffect(() => {
        adminService.getOrganizations().then(r => {
            const list = (r.data.organizations || []).map((o: any) => ({ id: o.id, name: o.name || `org ${o.id}` }));
            setOrgs(list);
        }).catch(() => { /* org names are a convenience only */ });
        adminService.getMemorySummary().then(r => setSummary(r.data)).catch(() => { /* optional */ });
    }, []);

    const filters = useMemo(() => ({
        profile_id: profileId,
        org_id: profileId ? undefined : (orgId || undefined),
        catalog: catalog.trim() || undefined,
        q: q || undefined,
        enabled: enabledF === '' ? undefined : enabledF === 'true',
    }), [orgId, catalog, q, enabledF, profileId]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            if (view === 'picks') {
                const resp = await adminService.listPickMemory({ ...filters, source: source || undefined, limit: PAGE, offset });
                setPicks(resp.data.rows);
                setTotal(resp.data.total);
            } else {
                const resp = await adminService.listComponentPairs({ ...filters, limit: PAGE, offset });
                setPairs(resp.data.rows);
                setTotal(resp.data.total);
            }
        } catch (e: any) {
            setError(e?.response?.data?.detail?.toString?.() || e?.message || 'Failed to load');
        } finally {
            setLoading(false);
        }
    }, [view, filters, source, offset]);

    useEffect(() => { load(); }, [load]);

    const orgLabel = (id: number | null) => id === null ? (profileId ? 'profile' : 'shared') : (orgs.find(o => o.id === id)?.name || `org ${id}`);

    const applySearch = (e: React.FormEvent) => {
        e.preventDefault();
        setOffset(0);
        setQ(qDraft.trim());
    };

    const refreshSummary = () => adminService.getMemorySummary().then(r => setSummary(r.data)).catch(() => undefined);

    // ---- picks: edit / toggle / delete / share
    const startEdit = (r: PickMemoryRow) => {
        setEditing(r.id);
        setDraft({ part_id: r.part_id, part_name: r.part_name || '' });
    };
    const saveEdit = async () => {
        if (editing === null || saving) return;
        setSaving(true);
        setError(null);
        try {
            const { row } = (await adminService.updatePickMemory(editing, { part_id: draft.part_id, part_name: draft.part_name || null })).data;
            setPicks(prev => prev.map(r => (r.id === row.id ? row : r)));
            setEditing(null);
        } catch (e: any) {
            setError(e?.response?.data?.detail || e?.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };
    const onEditKey = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') saveEdit();
        if (e.key === 'Escape') setEditing(null);
    };
    const togglePick = async (r: PickMemoryRow, on: boolean) => {
        try {
            const { row } = (await adminService.updatePickMemory(r.id, { enabled: on })).data;
            setPicks(prev => prev.map(x => (x.id === row.id ? row : x)));
        } catch (e: any) { setError(e?.response?.data?.detail || e?.message || 'Update failed'); }
    };
    const sharePick = async (r: PickMemoryRow) => {
        if (!window.confirm(`Share this pick with every organisation?\n${r.line_key} → ${r.part_id}`)) return;
        try {
            const { row } = (await adminService.updatePickMemory(r.id, { shared: true })).data;
            setPicks(prev => prev.map(x => (x.id === row.id ? row : x)));
        } catch (e: any) { setError(e?.response?.data?.detail || e?.message || 'Update failed'); }
    };
    const deletePick = async (r: PickMemoryRow) => {
        if (!window.confirm(`Forget this pick?\n${r.vehicle_key}${r.model_year || ''} · ${r.line_key} → ${r.part_id}`)) return;
        try {
            await adminService.deletePickMemory(r.id);
            setPicks(prev => prev.filter(x => x.id !== r.id));
            setTotal(t => Math.max(0, t - 1));
            refreshSummary();
        } catch (e: any) { setError(e?.response?.data?.detail || e?.message || 'Delete failed'); }
    };

    // ---- pairs: toggle / share / delete
    const togglePair = async (r: ComponentPairRow, on: boolean) => {
        try {
            const { row } = (await adminService.updateComponentPair(r.id, { enabled: on })).data;
            setPairs(prev => prev.map(x => (x.id === row.id ? row : x)));
        } catch (e: any) { setError(e?.response?.data?.detail || e?.message || 'Update failed'); }
    };
    const sharePair = async (r: ComponentPairRow) => {
        if (!window.confirm(`Share "${r.a}" = "${r.b}" with every organisation?`)) return;
        try {
            const { row } = (await adminService.updateComponentPair(r.id, { shared: true })).data;
            setPairs(prev => prev.map(x => (x.id === row.id ? row : x)));
        } catch (e: any) { setError(e?.response?.data?.detail || e?.message || 'Update failed'); }
    };
    const deletePair = async (r: ComponentPairRow) => {
        if (!window.confirm(`Forget that "${r.a}" = "${r.b}" on ${r.catalog}?`)) return;
        try {
            await adminService.deleteComponentPair(r.id);
            setPairs(prev => prev.filter(x => x.id !== r.id));
            setTotal(t => Math.max(0, t - 1));
            refreshSummary();
        } catch (e: any) { setError(e?.response?.data?.detail || e?.message || 'Delete failed'); }
    };

    // ---- add
    const submitAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        try {
            if (view === 'picks') {
                await adminService.createPickMemory({
                    organization_id: addPick.org && addPick.org !== 'shared' ? Number(addPick.org) : null,
                    catalog: addPick.catalog.trim(), vin: addPick.vin.trim(), line: addPick.line.trim(),
                    part_id: addPick.part_id.trim(), part_name: addPick.part_name.trim() || null,
                });
                setAddPick(a => ({ ...a, vin: '', line: '', part_id: '', part_name: '' }));
            } else {
                await adminService.createComponentPair({
                    organization_id: addPair.org && addPair.org !== 'shared' ? Number(addPair.org) : null,
                    catalog: addPair.catalog.trim(), a: addPair.a.trim(), b: addPair.b.trim(),
                });
                setAddPair(a => ({ ...a, a: '', b: '' }));
            }
            setMsg('Added.');
            load();
            refreshSummary();
        } catch (err: any) {
            setError(err?.response?.data?.detail || err?.message || 'Add failed');
        }
    };

    // ---- export / bulk delete (picks)
    const onDeleteAll = async () => {
        const what = view === 'picks' ? 'pick(s)' : 'equivalence(s)';
        if (!window.confirm(`Delete all ${total} ${what} matching the current filters? Rows inside a profile are not touched. This cannot be undone.`)) return;
        const typed = window.prompt(`Type the number of rows to delete (${total}) to confirm:`);
        if (typed === null) return;
        if (Number(typed) !== total) { setError(`Expected ${total}, got "${typed}" — nothing deleted.`); return; }
        setError(null);
        try {
            const common = { org_id: orgId || undefined, catalog: catalog.trim() || undefined, q: q || undefined, enabled: enabledF === '' ? undefined : enabledF === 'true', confirm: total };
            const r = view === 'picks'
                ? await adminService.deletePickMemoryBulk({ ...common, source: source || undefined })
                : await adminService.deleteComponentPairsBulk(common);
            setMsg(`Deleted ${r.data.deleted} ${what}.`);
            setOffset(0);
            load();
            refreshSummary();
        } catch (e: any) { setError(e?.response?.data?.detail || e?.message || 'Delete failed'); }
    };
    const onExport = async () => {
        try {
            const resp = await adminService.exportPickMemory({ org_id: orgId || undefined, catalog: catalog.trim() || undefined });
            const url = URL.createObjectURL(new Blob([resp.data], { type: 'text/csv;charset=utf-8' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `pick_memory_${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (e: any) {
            setError(e?.message || 'Export failed');
        }
    };

    const summaryCount = (kind: 'picks' | 'pairs', pred: (r: any) => boolean) =>
        (summary?.[kind] || []).filter(pred).reduce((n: number, r: any) => n + (r.n || 0), 0);

    return (
        <div className="space-y-4">
            <div className={`bg-white rounded-lg shadow p-4 space-y-3 ${compact ? 'border border-gray-100' : ''}`}>
                {!compact && <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Memory</h2>
                        <p className="text-sm text-gray-600">
                            What people taught the autopilot on live orders: a <b>pick</b> is the part a person chose when Cata asked a question about a request line on this vehicle family; an <b>equivalence</b> is two component names that turned out to mean the same part. Rows marked <i>seed</i> were imported by hand before profiles existed and can be deleted — trained knowledge now lives in profiles (back-office → Training).
                        </p>
                    </div>
                    {summary && (
                        <div className="text-xs text-gray-600 text-right space-y-0.5">
                            <div>Autopilot: <span className={summary.autopilot_enabled ? 'text-green-700 font-medium' : 'text-gray-500 font-medium'}>{summary.autopilot_enabled ? 'ON' : 'OFF'}</span> <span className="text-gray-400">(Algorithm settings)</span></div>
                            <div>Org memory: <span className={summary.org_memory_recorded ? 'text-green-700 font-medium' : 'text-gray-500 font-medium'}>{summary.org_memory_recorded ? 'recording answers' : 'not recording'}</span> · <span className={summary.org_memory_applied ? 'text-green-700 font-medium' : 'text-amber-700 font-medium'}>{summary.org_memory_applied ? 'applied' : 'not applied'}</span> <span className="text-gray-400">(PICK_MEMORY_RECORD_ORG / PICK_MEMORY_USE_ORG) — profile rows always apply</span></div>
                            <div>{summaryCount('picks', r => r.enabled)} picks on · {summaryCount('picks', r => !r.enabled)} off · {summaryCount('picks', r => r.source === 'confirmed')} confirmed by people</div>
                            <div>{summaryCount('pairs', r => r.enabled)} equivalences on · {summaryCount('pairs', r => !r.enabled)} off</div>
                        </div>
                    )}
                </div>}

                <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm w-fit">
                    {(['picks', 'pairs'] as View[]).map(v => (
                        <button key={v} onClick={() => { setView(v); setOffset(0); setEditing(null); setShowAdd(false); }}
                            className={`px-4 py-1.5 ${view === v ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
                            {v === 'picks' ? 'Picks' : 'Equivalences'}
                        </button>
                    ))}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <form onSubmit={applySearch} className="flex items-center gap-2 flex-1 min-w-[240px]">
                        <div className="relative flex-1">
                            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input value={qDraft} onChange={e => setQDraft(e.target.value)}
                                placeholder={view === 'picks' ? 'line text, part number, part name or VIN prefix' : 'component name'}
                                className="w-full pl-8 pr-2 py-2 border border-gray-300 rounded-md text-sm" />
                        </div>
                        <button type="submit" className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm">Search</button>
                    </form>
                    {!profileId && <select value={orgId} onChange={e => { setOffset(0); setOrgId(e.target.value); }} className="px-2 py-2 border border-gray-300 rounded-md text-sm">
                        <option value="">All organisations</option>
                        <option value="shared">Shared only</option>
                        {orgs.map(o => <option key={o.id} value={String(o.id)}>{o.name}</option>)}
                    </select>}
                    <input value={catalog} onChange={e => { setOffset(0); setCatalog(e.target.value); }} placeholder="Catalog (e.g. MG)"
                        className="w-32 px-2 py-2 border border-gray-300 rounded-md text-sm" />
                    {view === 'picks' && (
                        <select value={source} onChange={e => { setOffset(0); setSource(e.target.value); }} className="px-2 py-2 border border-gray-300 rounded-md text-sm">
                            <option value="">All sources</option>
                            <option value="confirmed">confirmed (a person answered a question)</option>
                            <option value="admin">admin (added here)</option>
                            <option value="seed">seed (imported by hand, pre-profiles)</option>
                            <option value="autopilot">autopilot</option>
                        </select>
                    )}
                    <select value={enabledF} onChange={e => { setOffset(0); setEnabledF(e.target.value as any); }} className="px-2 py-2 border border-gray-300 rounded-md text-sm">
                        <option value="">On + off</option>
                        <option value="true">Enabled</option>
                        <option value="false">Switched off</option>
                    </select>
                    <button onClick={load} className="p-2 border border-gray-300 rounded-md" title="Refresh">
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                    {!profileId && <button onClick={() => setShowAdd(s => !s)} className="flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-md text-sm">
                        <Plus className="w-4 h-4" /> Add
                    </button>}
                    {view === 'picks' && (
                        <button onClick={onExport} className="flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-md text-sm">
                            <Download className="w-4 h-4" /> Export CSV
                        </button>
                    )}
                    {!profileId && total > 0 && (
                        <button onClick={onDeleteAll} className="flex items-center gap-1 px-3 py-2 border border-red-300 text-red-700 rounded-md text-sm" title="Delete every row the current filters match (profile rows are never touched)">
                            <Trash2 className="w-4 h-4" /> Delete all {total}
                        </button>
                    )}
                </div>

                {showAdd && view === 'picks' && (
                    <form onSubmit={submitAdd} className="grid grid-cols-2 md:grid-cols-6 gap-2 p-3 bg-gray-50 rounded-md text-sm">
                        <select value={addPick.org} onChange={e => setAddPick(a => ({ ...a, org: e.target.value }))} className="px-2 py-1.5 border border-gray-300 rounded">
                            <option value="shared">Shared (every org)</option>
                            {orgs.map(o => <option key={o.id} value={String(o.id)}>{o.name}</option>)}
                        </select>
                        <input required value={addPick.catalog} onChange={e => setAddPick(a => ({ ...a, catalog: e.target.value }))} placeholder="Catalog" className="px-2 py-1.5 border border-gray-300 rounded" />
                        <input required value={addPick.vin} onChange={e => setAddPick(a => ({ ...a, vin: e.target.value }))} placeholder="VIN" className="px-2 py-1.5 border border-gray-300 rounded font-mono" />
                        <input required value={addPick.line} onChange={e => setAddPick(a => ({ ...a, line: e.target.value }))} placeholder="Request line as written" className="px-2 py-1.5 border border-gray-300 rounded" />
                        <input required value={addPick.part_id} onChange={e => setAddPick(a => ({ ...a, part_id: e.target.value }))} placeholder="Part number" className="px-2 py-1.5 border border-gray-300 rounded font-mono" />
                        <div className="flex gap-2">
                            <input value={addPick.part_name} onChange={e => setAddPick(a => ({ ...a, part_name: e.target.value }))} placeholder="Part name (optional)" className="flex-1 px-2 py-1.5 border border-gray-300 rounded" />
                            <button type="submit" className="px-3 py-1.5 bg-green-600 text-white rounded">Save</button>
                        </div>
                    </form>
                )}
                {showAdd && view === 'pairs' && (
                    <form onSubmit={submitAdd} className="grid grid-cols-2 md:grid-cols-5 gap-2 p-3 bg-gray-50 rounded-md text-sm">
                        <select value={addPair.org} onChange={e => setAddPair(a => ({ ...a, org: e.target.value }))} className="px-2 py-1.5 border border-gray-300 rounded">
                            <option value="shared">Shared (every org)</option>
                            {orgs.map(o => <option key={o.id} value={String(o.id)}>{o.name}</option>)}
                        </select>
                        <input required value={addPair.catalog} onChange={e => setAddPair(a => ({ ...a, catalog: e.target.value }))} placeholder="Catalog" className="px-2 py-1.5 border border-gray-300 rounded" />
                        <input required value={addPair.a} onChange={e => setAddPair(a => ({ ...a, a: e.target.value }))} placeholder='Reading A, e.g. "bumper cover"' className="px-2 py-1.5 border border-gray-300 rounded" />
                        <input required value={addPair.b} onChange={e => setAddPair(a => ({ ...a, b: e.target.value }))} placeholder='Reading B, e.g. "bumper fascia"' className="px-2 py-1.5 border border-gray-300 rounded" />
                        <button type="submit" className="px-3 py-1.5 bg-green-600 text-white rounded">Save</button>
                    </form>
                )}


                {msg && <p className="text-sm text-gray-700">{msg}</p>}
                {error && <p className="text-sm text-red-600">{String(error)}</p>}
            </div>

            <div className="bg-white rounded-lg shadow overflow-x-auto">
                {view === 'picks' ? (
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 text-left text-gray-600">
                            <tr>
                                <th className="px-3 py-2">Organisation</th>
                                <th className="px-3 py-2">Catalog</th>
                                <th className="px-3 py-2">Vehicle</th>
                                <th className="px-3 py-2">Request line</th>
                                <th className="px-3 py-2">Part</th>
                                <th className="px-3 py-2">Source</th>
                                <th className="px-3 py-2 text-right">Hits</th>
                                <th className="px-3 py-2">On</th>
                                <th className="px-3 py-2 w-28"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {picks.map(r => (
                                <tr key={r.id} className={`border-t border-gray-100 ${editing === r.id ? 'bg-blue-50/40' : ''} ${!r.enabled ? 'text-gray-400' : ''}`}>
                                    <td className="px-3 py-2">{orgLabel(r.organization_id)}</td>
                                    <td className="px-3 py-2">{r.catalog}</td>
                                    <td className="px-3 py-2 font-mono" title="VIN positions 1–8 + model-year code">{r.vehicle_key}<span className="text-gray-400">{r.model_year || '?'}</span></td>
                                    <td className="px-3 py-2">{r.line_key}</td>
                                    <td className="px-3 py-1">
                                        {editing === r.id ? (
                                            <div className="flex gap-1">
                                                <input autoFocus value={draft.part_id} onChange={e => setDraft(d => ({ ...d, part_id: e.target.value }))} onKeyDown={onEditKey}
                                                    className="w-36 px-2 py-1 border border-gray-300 rounded text-sm font-mono" />
                                                <input value={draft.part_name} onChange={e => setDraft(d => ({ ...d, part_name: e.target.value }))} onKeyDown={onEditKey}
                                                    placeholder="part name" className="w-48 px-2 py-1 border border-gray-300 rounded text-sm" />
                                            </div>
                                        ) : (
                                            <span onDoubleClick={() => startEdit(r)} title="Double-click to edit">
                                                <span className="font-mono">{r.part_id}</span>
                                                {r.part_name && <span className="text-gray-500"> · {r.part_name}</span>}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2">
                                        <span className={`px-1.5 py-0.5 rounded text-xs ${r.source === 'confirmed' ? 'bg-green-50 text-green-700' : r.source === 'seed' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{r.source}</span>
                                    </td>
                                    <td className="px-3 py-2 text-right">{r.hits}</td>
                                    <td className="px-3 py-2"><Toggle on={r.enabled} onChange={v => togglePick(r, v)} /></td>
                                    <td className="px-3 py-1 whitespace-nowrap">
                                        {editing === r.id ? (
                                            <>
                                                <button onClick={saveEdit} disabled={saving} className="p-1 text-green-700" title="Save (Enter)"><Check className="w-4 h-4" /></button>
                                                <button onClick={() => setEditing(null)} className="p-1 text-gray-500" title="Cancel (Esc)"><X className="w-4 h-4" /></button>
                                            </>
                                        ) : (
                                            <>
                                                <button onClick={() => startEdit(r)} className="p-1 text-gray-600" title="Edit part"><Pencil className="w-4 h-4" /></button>
                                                {r.organization_id !== null && (
                                                    <button onClick={() => sharePick(r)} className="p-1 text-xs text-blue-700" title="Share with every organisation">share</button>
                                                )}
                                                <button onClick={() => deletePick(r)} className="p-1 text-red-600" title="Forget"><Trash2 className="w-4 h-4" /></button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {!loading && picks.length === 0 && (
                                <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-500">Nothing remembered for these filters.</td></tr>
                            )}
                        </tbody>
                    </table>
                ) : (
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 text-left text-gray-600">
                            <tr>
                                <th className="px-3 py-2">Organisation</th>
                                <th className="px-3 py-2">Catalog</th>
                                <th className="px-3 py-2">Reading A</th>
                                <th className="px-3 py-2">Reading B</th>
                                <th className="px-3 py-2">Source</th>
                                <th className="px-3 py-2 text-right">Hits</th>
                                <th className="px-3 py-2">On</th>
                                <th className="px-3 py-2 w-24"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {pairs.map(r => (
                                <tr key={r.id} className={`border-t border-gray-100 ${!r.enabled ? 'text-gray-400' : ''}`}>
                                    <td className="px-3 py-2">{orgLabel(r.organization_id)}</td>
                                    <td className="px-3 py-2">{r.catalog}</td>
                                    <td className="px-3 py-2">{r.a}</td>
                                    <td className="px-3 py-2">{r.b}</td>
                                    <td className="px-3 py-2">
                                        <span className={`px-1.5 py-0.5 rounded text-xs ${r.source === 'confirmed' ? 'bg-green-50 text-green-700' : r.source === 'seed' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{r.source}</span>
                                    </td>
                                    <td className="px-3 py-2 text-right">{r.hits}</td>
                                    <td className="px-3 py-2"><Toggle on={r.enabled} onChange={v => togglePair(r, v)} /></td>
                                    <td className="px-3 py-1 whitespace-nowrap">
                                        {r.organization_id !== null && (
                                            <button onClick={() => sharePair(r)} className="p-1 text-xs text-blue-700" title="Share with every organisation">share</button>
                                        )}
                                        <button onClick={() => deletePair(r)} className="p-1 text-red-600" title="Forget"><Trash2 className="w-4 h-4" /></button>
                                    </td>
                                </tr>
                            ))}
                            {!loading && pairs.length === 0 && (
                                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">No learned equivalences for these filters.</td></tr>
                            )}
                        </tbody>
                    </table>
                )}
                <div className="flex items-center justify-between px-4 py-2 text-sm text-gray-600 border-t border-gray-100">
                    <span>{total} row{total === 1 ? '' : 's'}</span>
                    <div className="flex gap-2">
                        <button disabled={offset === 0} onClick={() => setOffset(o => Math.max(0, o - PAGE))} className="px-2 py-1 border border-gray-300 rounded disabled:opacity-40">Prev</button>
                        <button disabled={offset + PAGE >= total} onClick={() => setOffset(o => o + PAGE)} className="px-2 py-1 border border-gray-300 rounded disabled:opacity-40">Next</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MemoryTab;
